import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { toast } from "react-toastify";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import TablePagination from "src/components/table-pagination";
import { Button, PrimaryButton } from "src/components/base/button";
import { CheckCircleIcon, ChevronDownIcon, ClipboardDocumentIcon, XCircleIcon } from "@heroicons/react/20/solid";
import SETTINGS_CONTEXT from "src/context/internal/settings";
import { Strong, Text } from "src/components/base/text";
import React, { Suspense, useEffect } from "react";
import { Badge } from "src/components/base/badges/base";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "src/components/base/dropdown";
import { Dialog, DialogActions, DialogTitle } from "src/components/base/dialog";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import { useForm } from "@tanstack/react-form";
import WS from "src/api/ws";
import { UUID } from "src/api/api_common";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link PendingExitNodeOverview}
 */
export type PendingExitNodeOverviewProps = {};

const LIMIT = 25;

/**
 * Overview for all pending exit nodes
 */
export default function PendingExitNodeOverview(props: PendingExitNodeOverviewProps) {
    const [t] = useTranslation("admin-exit-nodes");
    const [tg] = useTranslation();
    const navigate = useNavigate();

    const [openApprove, setOpenApprove] = React.useState<UUID | undefined>();
    const [openReject, setOpenReject] = React.useState<UUID | undefined>();

    const { page, search } = Route.useSearch();
    const router = useRouter();

    const settingsContext = React.useContext(SETTINGS_CONTEXT);

    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.ExitNodesChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) => {
                                    await navigate({
                                        to: "/a/exitnodes/pending",
                                        search: {
                                            page: 1,
                                            search: fieldApi.state.value,
                                        },
                                    });
                                },
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.device-name")}</TableHeader>
                        <TableHeader>{t("label.last-seen")}</TableHeader>
                        <TableHeader>{t("label.addresses")}</TableHeader>
                        <TableHeader className={"max-lg:hidden"}>{t("label.first-login")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map(({ device, uuid, advertise }) => (
                        <TableRow href={"/a/devices/$deviceId/general"} params={{ deviceId: device.uuid }} key={uuid}>
                            <TableCell>
                                <div className={"flex flex-col gap-1"}>
                                    <div className={"flex gap-2"}>
                                        <Strong>{device.dnsname}</Strong>
                                        {!advertise && <Badge color={"teal"}>{tg("label.disabled")}</Badge>}
                                    </div>
                                    <div className={"flex flex-wrap gap-1"}>
                                        {device.tags?.map((tag) => (
                                            <TagBadge key={tag.uuid} tag={tag} />
                                        ))}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                {device.online ? (
                                    <div className={"flex items-center gap-2"}>
                                        <div className={"size-2 animate-pulse rounded-sm bg-green-500"} />
                                        <Text className={"max-lg:hidden"}>{t("label.online")}</Text>
                                    </div>
                                ) : device.last_seen ? (
                                    new Date(device.last_seen).toLocaleString("de-de")
                                ) : (
                                    "?"
                                )}
                            </TableCell>
                            <TableCell>
                                <div className={"flex items-center gap-1"}>
                                    <span>{device.ipv4}</span>
                                    <Dropdown>
                                        <DropdownButton plain={true}>
                                            <ChevronDownIcon />
                                        </DropdownButton>
                                        <DropdownMenu anchor={"bottom end"}>
                                            {[
                                                device.ipv4,
                                                device.ipv6,
                                                device.dnsname,
                                                ...settingsContext.settings.headscale_domains.map(
                                                    (domain) => `${device.dnsname}.${domain}`,
                                                ),
                                            ].map(
                                                (address) =>
                                                    address && (
                                                        <DropdownItem
                                                            key={address}
                                                            onClick={async () => {
                                                                await navigator.clipboard.writeText(address);
                                                                toast.success(tg("toast.copied-to-clipboard"));
                                                            }}
                                                        >
                                                            <ClipboardDocumentIcon />
                                                            <DropdownLabel>{address}</DropdownLabel>
                                                        </DropdownItem>
                                                    ),
                                            )}
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </TableCell>
                            <TableCell className={"max-lg:hidden"}>
                                {new Date(device.created_at).toLocaleDateString("de-de")}
                            </TableCell>
                            <TableCell>
                                <div className={"flex gap-1"}>
                                    <Button plain={true} onClick={() => setOpenApprove(uuid)}>
                                        <CheckCircleIcon />
                                    </Button>
                                    <Button plain={true} onClick={() => setOpenReject(uuid)}>
                                        <XCircleIcon />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/exitnodes/pending"}
                getSearchParams={(newPage: number) => ({ page: newPage })}
            />

            {openApprove && (
                <Suspense>
                    <Dialog open={true} onClose={() => setOpenApprove(undefined)}>
                        <DialogTitle>{t("heading.approve-exit-node")}</DialogTitle>

                        <DialogActions>
                            <Button plain={true} onClick={() => setOpenApprove(undefined)}>
                                {tg("button.cancel")}
                            </Button>
                            <PrimaryButton
                                onClick={async () => {
                                    openApprove && (await ApiInternal.exitNode.approve(openApprove));
                                    setOpenApprove(undefined);
                                }}
                            >
                                {t("button.approve")}
                            </PrimaryButton>
                        </DialogActions>
                    </Dialog>
                </Suspense>
            )}

            {openReject && (
                <Suspense>
                    <Dialog open={true} onClose={() => setOpenReject(undefined)}>
                        <DialogTitle>{t("heading.reject-exit-node")}</DialogTitle>

                        <DialogActions>
                            <Button plain={true} onClick={() => setOpenReject(undefined)}>
                                {tg("button.cancel")}
                            </Button>
                            <Button
                                color={"red"}
                                onClick={async () => {
                                    openReject && (await ApiInternal.exitNode.reject(openReject));
                                    setOpenReject(undefined);
                                }}
                            >
                                {t("button.reject")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Suspense>
            )}
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type PendingExitNodesOverviewSearch = {
    /** The current page number we're on */
    page: number;
    /** The search term that should be used */
    search: string;
};

export const Route = createFileRoute("/_admin/a/exitnodes/_exitnodes/pending")({
    component: PendingExitNodeOverview,
    validateSearch: (search: Record<string, unknown>): PendingExitNodesOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
        };
    },
    loaderDeps: ({ search: { page, search } }) => ({ page, search }),
    loader: async ({ deps }) =>
        await ApiInternal.exitNode.getPending({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
        }),
});
